
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import { Validate } from '@/types';
import { PERMISSION } from '@/permissions';
import CustomBranding from '@/components/customize/CustomBranding.vue';
import CustomTheme from '@/components/customize/CustomTheme.vue';
import CustomFont from '@/components/customize/CustomFont.vue';
import CustomMenuBrowseStyles from '@/components/customize/CustomMenuBrowseStyles.vue';
import CustomCardStyles from '@/components/customize/CustomCardStyles.vue';
import CustomLocales from '@/components/customize/Locales.vue';

@Component<AppearanceSettings>({
	components: {
		ValidationObserver,
		CustomBranding,
		CustomTheme,
		CustomFont,
		CustomMenuBrowseStyles,
		CustomCardStyles,
		CustomLocales
	}
})
export default class AppearanceSettings extends Vue {
	@Prop({ type: Object, required: true }) private restaurant!: Restaurant;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Watch('restaurant')
	private onRestaurantChange(): void {
		this.setAppearanceData();
	}

	$refs!: { observer: Validate };

	private PERMISSION = PERMISSION;
	private invalidColor: boolean = false;
	private menuConfig: MenuConfiguration|object|null = null;
	private name: string = '';
	private displayName: string = '';
	private image: string = '';
	private imageFile: string | null = null;
	private locales: RestaurantLocale[]|null = null;
	private multipleLocales: boolean = false;

	private created(): void {
		this.setAppearanceData();
	}

	private setAppearanceData(): void {
		this.menuConfig = this.restaurant.menu_configuration ? this.restaurant.menu_configuration : { use_default_fonts: true };
		this.locales = this.restaurant.locales;
		this.multipleLocales = !!(this.restaurant.locales && this.restaurant.locales.length);
		this.name = this.restaurant.name;
		this.displayName = this.restaurant.display_name;
		this.image = this.restaurant.image;
	}

	/**
	 * Update branding info (display name, image)
	 *
	 * @return {void}
	 */
	private updateBrandingInfo(object: any): void {
		this.displayName = object.displayName;
		this.image = object.image;
		this.imageFile = object.imageFile;
	}

	/**
	 * Update restaurant locales
	 *
	 * @return {RestaurantLocale[]}
	 * @return {void}
	 */
	private updateRestaurantLocales(restaurantLocales: RestaurantLocale[]): void {
		this.locales = restaurantLocales;
	}

	/**
	 * Update menu configuration
	 *
	 * @param {MenuConfiguration} updatedMenuConfig
	 * @return {void}
	 */
	private updateMenuConfig(updatedMenuConfig: MenuConfiguration): void {
		this.menuConfig = { ...this.menuConfig, ...updatedMenuConfig };
	}

	/**
	 * Validate appearance settings and emit save event to parent component
	 * Is called by Settings.vue when the save button is clicked
	 *
	 * @return {Promise<void>}
	 */
	public async save(): Promise<void> {
		const isValid = await this.$refs.observer.validate();

		if (isValid) {
			// Allow empty colors so that we can fallback on the default one
			// We need to actually delete the field since the backend does not
			// allow its values to be empty
			if (!(this.menuConfig as MenuConfiguration).primary_color) {
				delete (this.menuConfig as MenuConfiguration).primary_color;
			}

			if (!(this.menuConfig as MenuConfiguration).primary_font) {
				delete (this.menuConfig as MenuConfiguration).primary_font;
			}

			if (!(this.menuConfig as MenuConfiguration).secondary_font) {
				delete (this.menuConfig as MenuConfiguration).secondary_font;
			}

			const updatedRestaurant = {
				...this.restaurant,
				menu_configuration: this.menuConfig,
				display_name: this.displayName,
				image: this.image,
				locales: this.locales && this.locales.length ? this.locales : null
			};

			// Pass imageFile as additional data if present
			const additionalData = this.imageFile ? { imageFile: this.imageFile } : undefined;

			this.$emit('save-appearance', updatedRestaurant, additionalData);
		}
	}

}
